import { ScoreType } from "@/enums/ScoreType";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";

// The keys under developing entitlements may not be final.
// once finalized, remove from here and add to the defaults.
export interface DevelopingUserEntitlement {
  assess: boolean;
  instruct: boolean;
  tutor: boolean;
  math: boolean;
  lectura: boolean;
}

// set the initial values, you can test by changing the values here.
export const developingEntitlements: DevelopingUserEntitlement = {
  assess: false,
  instruct: false,
  tutor: false,
  math: false,
  lectura: false,
};

export interface UserEntitlementData {
  subscriptions: DevelopingUserEntitlement;
  curriculumOnboardCompleted: boolean;
  assessmentOnboardingCompleted: boolean;
  curriculumRightCertificationAcknowledged: boolean;
  branding: string | null;
  reportingLanguage: string;
  dyslexiaEnabled: boolean;
  partner: string;
  dyslexiaParentLetterEnabled: boolean;
  parentReportEnabled: boolean;
  legacyReportsEnabled: boolean;
  texasResources: boolean;
  schoolYearStartDate: string | null;
  resourceProviders: string[] | null;
  statePercentileCuts: string | null;
  prereaderAudioEnabled: boolean | null;
  useLatestARM: boolean;
  demoLockdown: boolean;
  calibrationEnabled: boolean;
  demoMode: boolean;
  iesEnabled: boolean;
  districtAdminBadgingEnabled: boolean;
  expandedEarlyReaderEnabled: boolean;
  badgingEnabled: boolean;
  windowDisabledByGrade: [] | null;
  showBadNetworkResponse: boolean;
  curriculum: string | null;
  fourCutlineCategoriesEnabled: boolean;
  allowLetterNamingAndSoundsRescoring: boolean;
  individualLearningPathMode: boolean;
  scoreType: ScoreType;
  notRosteringOnly: boolean;
  sorConfig: {
    selectedStandard: string | null;
  };
}

export interface UserEntitlementState {
  loading: boolean;
  data: UserEntitlementData;
}

export const defaultEntitlements: UserEntitlementData = {
  subscriptions: { ...developingEntitlements },
  curriculumOnboardCompleted: false,
  assessmentOnboardingCompleted: false,
  curriculumRightCertificationAcknowledged: false,
  branding: null,
  reportingLanguage: "en",
  dyslexiaEnabled: true,
  partner: "Amira",
  dyslexiaParentLetterEnabled: false,
  parentReportEnabled: true,
  legacyReportsEnabled: false,
  texasResources: false,
  schoolYearStartDate: null,
  resourceProviders: [],
  statePercentileCuts: null,
  prereaderAudioEnabled: null,
  useLatestARM: false,
  demoLockdown: false,
  calibrationEnabled: false,
  demoMode: false,
  iesEnabled: false,
  districtAdminBadgingEnabled: false,
  expandedEarlyReaderEnabled: false,
  badgingEnabled: false,
  individualLearningPathMode: false,
  windowDisabledByGrade: null,
  showBadNetworkResponse: false,
  curriculum: null,
  fourCutlineCategoriesEnabled: false,
  allowLetterNamingAndSoundsRescoring: false,
  scoreType: ScoreType.ARM,
  notRosteringOnly: false,
  sorConfig: {
    selectedStandard: null,
  },
};

export const initialState: UserEntitlementState = {
  loading: true,
  data: defaultEntitlements,
};

const userEntitlementSlice = createSlice({
  name: "userEntitlement",
  initialState,
  reducers: {
    setUserEntitlement(state, action: PayloadAction<UserEntitlementData>) {
      state.loading = false;
      state.data = action.payload;
      return state;
    },
  },
});

export const { setUserEntitlement } = userEntitlementSlice.actions;

export const selectUserEntitlement = (state: {
  userEntitlement: UserEntitlementState;
}) => state.userEntitlement;

export default userEntitlementSlice.reducer;
